import GlobalStyle from "./shared/globalStyles";
import { ThemeProvider } from "styled-components";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Home from "./pages/home/Home";
import Dashboard from "./pages/statistics/Statistics";
import Login from "./pages/login/Login";
import Signup from "./pages/signup/Signup";

import List from "./pages/list/List";
import Single from "./pages/single/Single";
import New from "./pages/new/New";
import Events from "./pages/events/Events";
import NewEvent from './pages/events/NewEvent';

import Maps from "./pages/maps/Maps";
import NewMap from "./pages/maps/NewMap";
import DrawMap from "./pages/maps/DrawMap";

import Companys from "./pages/companys/Companys";
import NewCompany from './pages/companys/NewCompany';
import CompanyOverview from "./pages/companys/CompanyOverview";

import Applicants from './pages/applicants/Applicants';
import NewApplicant from './pages/applicants/NewApplicant';
import SingleApplicant from './pages/applicants/SingleView';

import Exhibitors from "./pages/exhibitors/Exhibitors";
import FloorPlan from "./pages/floorplan/FloorPlan";
import Users from "./pages/users/Users";
import Profile from "./pages/profile/Profile";

import Colleagues from "./pages/colleagues/Colleagues";

import {
  formOrderInput,
  formProductInput,
  formUserInputs,
  formEventInput,
  formMapInput,
  formCompanyInput,
  formApplicantInput,

} from "./util/formDataSource";
import React, { useContext } from "react";
import { DarkModeContext } from "./context/darkModeContext";
import { darkTheme, lightTheme } from "./shared/theme";
import { AuthContext } from "./context/AuthContext";
import RequirePermission from "./components/auth/RequirePermission";
import NewColleague from "./pages/colleagues/NewColleague";
import Permissions from "./pages/permissions/Permissions";
import EditPermission from "./pages/permissions/EditPermission";
import Unauthorized from "./pages/unauthorized/Unauthorized";
import 'react-toastify/dist/ReactToastify.css';
import Products from "./pages/products/Products";
import NewProduct from './pages/products/NewProduct';
import EventProducts from "./pages/events/products/Products";
import NewEventProduct from './pages/events/products/NewProduct';

import Orders from "./pages/orders/Orders";
import ViewOrder from './pages/orders/ViewOrder';
import System from "./pages/maintenance/System";
import Proposals from "./pages/magazine/Proposals";
import ProposalDetail from "./pages/magazine/ProposalDetail";
import Publications from "./pages/magazine/Publications";
import Issues from "./pages/magazine/Issues";
import Inventory from "./pages/magazine/Inventory";
import MagazineReports from "./pages/magazine/MagazineReports";
import ReportsHub from "./pages/reports/ReportsHub";
import PipelineReport from "./pages/reports/PipelineReport";
import RepPerformanceReport from "./pages/reports/RepPerformanceReport";
import RevenueReport from "./pages/reports/RevenueReport";
import Contacts from "./pages/crm/Contacts";
import ContactDetail from "./pages/crm/ContactDetail";
import BookingsHub from "./pages/bookings/BookingsHub";
import ContractTemplates from "./pages/contracts/ContractTemplates";
import Contracts from "./pages/contracts/Contracts";
import ContractDetail from "./pages/contracts/ContractDetail";
import Invoices from "./pages/invoices/Invoices";
import InvoiceDetail from "./pages/invoices/InvoiceDetail";
import QuickBooksSettings from "./pages/quickbooks/QuickBooksSettings";
import MenuAccessGuard from "./components/auth/MenuAccessGuard";

function App() {
  const { isDarkMode } = useContext(DarkModeContext);

  // Authentication
  const { user } = useContext(AuthContext);
  interface AuthProps {
    children: JSX.Element;
  }
  const RequireAuth: React.FC<AuthProps> = ({ children }) => {
    return !!user ? children : <Navigate to="/login" />;
  };

  const RequireAdmin: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="users.manage">{children}</RequirePermission>
  );

  const RequireOps: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="ops.read">{children}</RequirePermission>
  );

  const RequireOpsEditor: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="ops.write">{children}</RequirePermission>
  );

  const RequireMagazineAccess: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="magazine.access">{children}</RequirePermission>
  );

  const RequireCompaniesAccess: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="companies.read">{children}</RequirePermission>
  );

  const RequireContactEditor: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="contacts.write">{children}</RequirePermission>
  );

  const RequireCompanyEditor: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="companies.write">{children}</RequirePermission>
  );

  const RequireMagazineReporter: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="magazine.reports">{children}</RequirePermission>
  );

  const RequireSalesReports: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="reports.read">{children}</RequirePermission>
  );

  const RequireQuickBooksManage: React.FC<AuthProps> = ({ children }) => (
    <RequirePermission permission="quickbooks.manage">{children}</RequirePermission>
  );

  // Set search data
  // setSearch();

  return (
    // THEME PROVIDER FOR THEME SWITCHING
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      {/* GLOBAL STYLES */}
      <GlobalStyle />
      <ToastContainer />
      <Router>
        <MenuAccessGuard>
        <Routes>
          <Route path="/">
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<Signup />} />
            <Route
              path="unauthorized"
              element={
                <RequireAuth>
                  <Unauthorized />
                </RequireAuth>
              }
            />
            {/* <Route path="exhibitors" element={<Exhibitors />} /> */}
            <Route path="exhibitors">
              <Route
                path=":id"
                element={
                  <Exhibitors />
                }
              />
            </Route>
            <Route path="floorplans">
              <Route
                path=":id"
                element={
                  <FloorPlan />
                }
              />
            </Route>
            <Route
              index
              element={
                <RequireAuth>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route
              path="dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route path="reports">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireSalesReports>
                      <ReportsHub />
                    </RequireSalesReports>
                  </RequireAuth>
                }
              />
              <Route
                path="pipeline"
                element={
                  <RequireAuth>
                    <RequireSalesReports>
                      <PipelineReport />
                    </RequireSalesReports>
                  </RequireAuth>
                }
              />
              <Route
                path="rep-performance"
                element={
                  <RequireAuth>
                    <RequireSalesReports>
                      <RepPerformanceReport />
                    </RequireSalesReports>
                  </RequireAuth>
                }
              />
              <Route
                path="revenue"
                element={
                  <RequireAuth>
                    <RequireSalesReports>
                      <RevenueReport />
                    </RequireSalesReports>
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              path="statistics"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="home"
              element={
                <RequireAuth>
                  <Home />
                </RequireAuth>
              }
            />
            <Route path="users">
              <Route
                index
                element={
                  <RequireAuth>
                    <Users />
                  </RequireAuth>
                }
              />
              <Route
                path="profile"
                element={
                  <RequireAuth>
                    <Profile userInputs={formUserInputs} />
                  </RequireAuth>
                }
              />
              {/* <Route
                path=":id"
                element={
                  <RequireAuth>
                    <Single />
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <New userInputs={formUserInputs} />
                  </RequireAuth>
                }
              /> */}
            </Route>
            <Route path="products">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireOps>
                      <Products />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <NewProduct actionMode="edit" />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireOpsEditor>
                      <NewProduct />
                    </RequireOpsEditor>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="orders">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireOps>
                      <Orders />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <ViewOrder />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              {/* <Route
                path="new"
                element={
                  <RequireAuth>
                    <New userInputs={formOrderInput} />
                  </RequireAuth>
                }
              /> */}
            </Route>
            <Route path="events">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireOps>
                      <Events />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireOpsEditor>
                      <NewEvent eventInputs={formEventInput} actionMode="new" />
                    </RequireOpsEditor>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <NewEvent eventInputs={formEventInput} actionMode="edit" />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route path=":id/products">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <RequireOps>
                        <EventProducts />
                      </RequireOps>
                    </RequireAuth>
                  }
                />
                <Route
                  path=":eventproduct"
                  element={
                    <RequireAuth>
                      <RequireOps>
                        <NewEventProduct actionMode="edit" />
                      </RequireOps>
                    </RequireAuth>
                  }
                />
                <Route
                  path="new"
                  element={
                    <RequireAuth>
                      <RequireOpsEditor>
                        <NewEventProduct actionMode="new" />
                      </RequireOpsEditor>
                    </RequireAuth>
                  }
                />
              </Route>
            </Route>
            <Route path="maps">
              <Route index
                element={
                  <RequireAuth>
                    <RequireOps>
                      <Maps />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <NewMap mapInputs={formMapInput} actionMode={'edit'} />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path="draw/:id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <DrawMap />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireOpsEditor>
                      <NewMap mapInputs={formMapInput} actionMode={'new'} />
                    </RequireOpsEditor>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="companys">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireCompaniesAccess>
                      <Companys />
                    </RequireCompaniesAccess>
                  </RequireAuth>
                }
              />
              <Route
                path=":id/overview"
                element={
                  <RequireAuth>
                    <RequireCompaniesAccess>
                      <CompanyOverview />
                    </RequireCompaniesAccess>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireCompanyEditor>
                      <NewCompany Inputs={formCompanyInput} actionMode="edit" />
                    </RequireCompanyEditor>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireCompanyEditor>
                      <NewCompany Inputs={formCompanyInput} actionMode="new" />
                    </RequireCompanyEditor>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="crm">
              <Route
                path="contacts"
                element={
                  <RequireAuth>
                    <RequireContactEditor>
                      <Contacts />
                    </RequireContactEditor>
                  </RequireAuth>
                }
              />
              <Route
                path="contacts/:id"
                element={<RequireAuth><RequireContactEditor><ContactDetail /></RequireContactEditor></RequireAuth>}
              />
            </Route>
            <Route path="bookings">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <BookingsHub />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="applicants">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireOps>
                      <Applicants />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireOps>
                      <NewApplicant Inputs={formApplicantInput} actionMode="edit" />
                    </RequireOps>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireOpsEditor>
                      <NewApplicant Inputs={formApplicantInput} actionMode="new" />
                    </RequireOpsEditor>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="colleagues">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Colleagues />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <NewColleague inputs={formUserInputs} actionMode="edit" />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="new"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <NewColleague inputs={formUserInputs} actionMode="new" />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="permissions">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Permissions />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <EditPermission inputs={formUserInputs} actionMode="edit" />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="contracts">
              <Route
                path="templates"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <ContractTemplates />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Contracts />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <ContractDetail />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="invoices">
              <Route
                index
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Invoices />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <InvoiceDetail />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              path="quickbooks"
              element={
                <RequireAuth>
                  <RequireQuickBooksManage>
                    <QuickBooksSettings />
                  </RequireQuickBooksManage>
                </RequireAuth>
              }
            />
            <Route path="magazine">
              <Route
                index
                element={<Navigate to="publications" replace />}
              />
              <Route
                path="publications"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Publications />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path="issues"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Issues />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path="inventory"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Inventory />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path="proposals"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <Proposals />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path="proposals/:id"
                element={
                  <RequireAuth>
                    <RequireMagazineAccess>
                      <ProposalDetail />
                    </RequireMagazineAccess>
                  </RequireAuth>
                }
              />
              <Route
                path="reports"
                element={
                  <RequireAuth>
                    <RequireMagazineReporter>
                      <MagazineReports />
                    </RequireMagazineReporter>
                  </RequireAuth>
                }
              />
              <Route
                path="bookings"
                element={<Navigate to="/magazine/proposals" replace />}
              />
            </Route>
            <Route path="maintenance">
              <Route
                path="system"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <System />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
            </Route>
          </Route>
        </Routes>
        </MenuAccessGuard>
      </Router>
    </ThemeProvider>
  );
}

export default App;
